import classNames from 'classnames'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { SelectOptionGroup } from 'web/data/models'
import { useOutsideClick } from 'web/hooks/useOutsideClick'
import { expectOneByPredicate, updateIndexCircular } from 'web/utils/utils'

type CustomSelectProps = {
    input: {
        bsSize: 'sm' | 'md' | 'lg' | 'xl'
        disabled?: boolean
    }
    selected?: string
    items: SelectOptionGroup[]
    onChange: (value: string) => void
    customSelectedItemRender?: (item: { value: string; label: string }) => string
}

export const CustomSelect = (props: CustomSelectProps): JSX.Element | null => {

    const containerRef = useRef<HTMLDivElement>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const selectItemRef = useRef<HTMLDivElement>(null)

    const [showDropdown, setShowDropdown] = useState(false)
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null)

    const innerItems = props.items.flatMap(item => item.items)

    // Keep the dropdown at least as wide as the input element, the
    // dropdown is allowed to grow beyond that width thought
    useLayoutEffect(() => {
        if (containerRef.current && dropdownRef.current) {
            dropdownRef.current.style.minWidth = `${containerRef.current.getBoundingClientRect().width}px`

            const top = dropdownRef.current.getBoundingClientRect().top
            const viewport = window.innerHeight

            dropdownRef.current.style.maxHeight = `${(viewport * 0.9) - top}px`
        }
    })

    useOutsideClick({
        ref: containerRef,
        onOutsideClick: () => {
            setShowDropdown(false)
            // setSelectedIndex(null)
        }
    })

    useEffect(() => {
        if (selectItemRef.current) {
            selectItemRef.current.scrollIntoView({
                behavior: 'auto',
                block: 'end'
            })
        }
    }, [selectedIndex, showDropdown])

    const onChange = (value: string) => {
        if (value === props.selected) {
            return
        }

        props.onChange(value)
    }

    return (
        <div
            ref={containerRef}
            className="user-select-none flex-grow-1"
            style={{
                position: 'relative'
            }}
            onKeyDown={(e) => {
                if (e.key === 'Tab') {
                    setShowDropdown(false)
                }
            }}
        >

            {/* Fake input to capture events on and display selected item */}
            <div
                tabIndex={props.input.disabled ? -1 : 0}
                className={classNames(
                    `form-control form-control-${props.input.bsSize} d-flex align-items-center`,
                    { 'disabled-input': props.input.disabled }
                )}
                style={{
                    pointerEvents: props.input.disabled ? 'none' : undefined,
                    padding: `0.6em 2em 0.6em 0.6em`,
                    background: props.input.disabled === true
                        ? undefined
                        : `#fff url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='4' height='5' viewBox='0 0 4 5'%3e%3cpath fill='%23343a40' d='M2 0L0 2h4zm0 5L0 3h4z'/%3e%3c/svg%3e") right 0.75rem center/8px 10px no-repeat`
                }}
                onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') {
                        e.preventDefault()
                        setShowDropdown(true)
                        setSelectedIndex(updateIndexCircular(selectedIndex, 0, innerItems.length - 1, 'down'))
                    }
                    if (e.key === 'ArrowUp') {
                        e.preventDefault()
                        setShowDropdown(true)
                        setSelectedIndex(updateIndexCircular(selectedIndex, 0, innerItems.length - 1, 'up'))
                    }
                    if (e.key === ' ') {
                        e.preventDefault()
                        if (!showDropdown) {
                            setShowDropdown(true)
                        }
                    }
                    if (e.key === 'Enter') {
                        e.preventDefault()

                        if (selectedIndex == null) {
                            return
                        }

                        onChange(innerItems[selectedIndex].value)

                        setShowDropdown(false)
                        setSelectedIndex(null)
                    }
                    if (e.key === 'Escape') {
                        e.preventDefault()
                        setShowDropdown(false)
                        setSelectedIndex(null)
                    }
                }}
                onMouseDown={() => {
                    setShowDropdown(!showDropdown)
                    setSelectedIndex(null)
                }}
            >
                {props.selected != null
                    ? props.customSelectedItemRender
                        ? props.customSelectedItemRender(expectOneByPredicate(innerItems, item => item.value === props.selected))
                        : expectOneByPredicate(innerItems, item => item.value === props.selected).label
                    : "Select..."
                }
            </div>

            {showDropdown && (
                <div
                    ref={dropdownRef}
                    className={classNames(
                        "rounded bg-white mt-2 p-1 border shadow overflow-y-auto pb-0-last-child",
                        // { 'small': props.input.bsSize === 'sm' }
                    )}
                    style={{
                        position: 'absolute',
                        zIndex: 10,
                        scrollbarColor: 'silver whitesmoke',
                        scrollbarWidth: 'thin',
                        whiteSpace: 'nowrap',
                        minWidth: 220,
                        overscrollBehavior: 'contain'
                    }}
                >

                    {props.items.map((item, index) => (
                        // Outer wrapper
                        <div
                            key={item.label}
                            className={classNames(
                                "pb-1",
                                { 'rounded': index === props.items.length - 1 }
                            )}
                        >
                            {/* Group header */}
                            <div
                                className="d-flex align-items-center px-1 py-1 rounded bg-light mb-1"
                            >
                                {item.imgUrl != null && (
                                    <img
                                        alt=''
                                        src={item.imgUrl}
                                        height={24}
                                        className="mr-2 border border-dark rounded"
                                    />
                                )}
                                <b>
                                    {item.label}
                                </b>
                            </div>

                            {/* Group items */}
                            {item.items.map((innerItem) => {

                                const currIndex = innerItems.findIndex(i => i.value === innerItem.value)
                                const isSelected = currIndex === selectedIndex || innerItem.value === props.selected

                                return (
                                    <div
                                        ref={isSelected ? selectItemRef : null}
                                        key={innerItem.value}
                                        className={classNames(
                                            "px-2 py-1 rounded bg-dark-hover cursor-pointer",
                                            { "bg-dark text-white": isSelected }
                                        )}
                                        onClick={() => {
                                            onChange(innerItem.value)
                                            setShowDropdown(false)
                                            setSelectedIndex(null)
                                        }}
                                    >
                                        {innerItem.label}
                                    </div>
                                )
                            })}
                        </div>
                    ))}

                </div>
            )}

        </div>
    )
}
